/**
 * Session idle clocks (#2212, #2213, #2214, #2216).
 *
 * One shared last-activity timestamp, two thresholds from tenant settings:
 * lock (default 15 min) and logout (default 480 min). The warning is about
 * lock, not sign-out. Lock is an overlay; logout uses the same mutation as
 * the header, including the Paya ID end-session hop while locked.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuthStore } from '@/stores/authStore';
import { useLockSession, useSignOut } from '@/hooks/useAuth';
import {
  DEFAULT_IDLE_LOCK_MINUTES,
  DEFAULT_IDLE_LOGOUT_MINUTES,
  useTenantSettings,
} from '@/hooks/useTenantSettings';
import { persistLoginContinuation } from './loginContinuation';
import { IDLE_CLOCK_STORAGE_KEY, readIdleClock, writeIdleClock } from './idleClock';
import { LockScreen } from './LockScreen';
import {
  INACTIVITY_LOGOUT_REASON,
  broadcastSessionLogout,
  subscribeToSessionBroadcast,
  suppressSessionBroadcast,
} from './sessionBroadcast';

export const DEFAULT_IDLE_LOCK_MS = DEFAULT_IDLE_LOCK_MINUTES * 60 * 1000;
export const DEFAULT_IDLE_LOGOUT_MS = DEFAULT_IDLE_LOGOUT_MINUTES * 60 * 1000;
export const DEFAULT_WARNING_BEFORE_LOCK_MS = 5 * 60 * 1000;
const WARNING_FLOOR_MS = 15 * 1000;
const ACTIVITY_THROTTLE_MS = 30 * 1000;

/** @deprecated Use lockMs from tenant settings. Kept for tests that pin the lock window. */
export const SESSION_TIMEOUT_MS = DEFAULT_IDLE_LOCK_MS;
export const WARNING_BEFORE_TIMEOUT_MS = DEFAULT_WARNING_BEFORE_LOCK_MS;

export function warningBeforeLockMs(lockMs: number): number {
  if (lockMs <= DEFAULT_WARNING_BEFORE_LOCK_MS * 2) {
    return Math.max(WARNING_FLOOR_MS, Math.floor(lockMs / 3));
  }
  return DEFAULT_WARNING_BEFORE_LOCK_MS;
}

function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function SessionTimeout() {
  const { t } = useTranslation('common');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLocked = useAuthStore((s) => s.isLocked);
  const signOut = useSignOut();
  const lockSession = useLockSession();
  const queryClient = useQueryClient();
  const { idleLockMinutes, idleLogoutMinutes } = useTenantSettings();

  const lockMs = idleLockMinutes * 60 * 1000;
  const logoutMs = idleLogoutMinutes * 60 * 1000;
  const warnMs = warningBeforeLockMs(lockMs);

  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(warnMs);
  const lastActivityUpdate = useRef<number>(0);
  const endingRef = useRef(false);
  const lockingRef = useRef(false);
  const signOutRef = useRef(signOut);
  const lockSessionRef = useRef(lockSession);
  const lockMsRef = useRef(lockMs);
  const logoutMsRef = useRef(logoutMs);
  const warnMsRef = useRef(warnMs);
  signOutRef.current = signOut;
  lockSessionRef.current = lockSession;
  lockMsRef.current = lockMs;
  logoutMsRef.current = logoutMs;
  warnMsRef.current = warnMs;

  const logoutDueToInactivity = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    setShowWarning(false);

    const returnTo = currentReturnPath();
    broadcastSessionLogout(INACTIVITY_LOGOUT_REASON);
    await signOutRef.current({
      reason: INACTIVITY_LOGOUT_REASON,
      returnTo,
    });
  }, []);

  const lockDueToInactivity = useCallback(async () => {
    if (endingRef.current || lockingRef.current) return;
    if (useAuthStore.getState().isLocked) return;
    lockingRef.current = true;
    setShowWarning(false);
    try {
      await lockSessionRef.current();
    } catch {
      endingRef.current = false;
      await logoutDueToInactivity();
    } finally {
      lockingRef.current = false;
    }
  }, [logoutDueToInactivity]);

  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;
    if (endingRef.current) return;
    if (useAuthStore.getState().isLocked) return;
    if (showWarning) return;

    const now = Date.now();
    const last = readIdleClock();
    if (last !== null) {
      const since = now - last;
      if (since >= logoutMsRef.current) {
        void logoutDueToInactivity();
        return;
      }
      if (since >= lockMsRef.current) {
        void lockDueToInactivity();
        return;
      }
      if (since >= lockMsRef.current - warnMsRef.current) {
        return;
      }
    }

    if (now - lastActivityUpdate.current > ACTIVITY_THROTTLE_MS) {
      lastActivityUpdate.current = now;
      writeIdleClock(now);
    }
  }, [isAuthenticated, showWarning, logoutDueToInactivity, lockDueToInactivity]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isLocked) return;

    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const throttledActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        handleActivity();
      }, 1000);
    };

    const directEvents = ['mousedown', 'keydown', 'touchstart'] as const;
    const throttledEvents = ['scroll', 'mousemove'] as const;

    directEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    throttledEvents.forEach((event) => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    if (readIdleClock() === null) {
      writeIdleClock(Date.now());
    }

    return () => {
      directEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      throttledEvents.forEach((event) => {
        window.removeEventListener(event, throttledActivity);
      });
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [isAuthenticated, isLocked, handleActivity]);

  useEffect(() => {
    if (!isAuthenticated) {
      endingRef.current = false;
      lockingRef.current = false;
      return;
    }

    const tick = () => {
      if (endingRef.current) return;
      const last = readIdleClock();
      if (last === null) {
        writeIdleClock(Date.now());
        return;
      }
      const since = Date.now() - last;
      if (since >= logoutMsRef.current) {
        void logoutDueToInactivity();
        return;
      }
      if (useAuthStore.getState().isLocked) {
        setShowWarning(false);
        return;
      }
      if (since >= lockMsRef.current) {
        void lockDueToInactivity();
        return;
      }
      const remainingToLock = lockMsRef.current - since;
      if (remainingToLock <= warnMsRef.current) {
        setShowWarning(true);
        setTimeRemaining(remainingToLock);
      } else {
        setShowWarning(false);
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== IDLE_CLOCK_STORAGE_KEY) return;
      tick();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated, idleLockMinutes, idleLogoutMinutes, logoutDueToInactivity, lockDueToInactivity]);

  useEffect(() => {
    if (!isAuthenticated) return;

    return subscribeToSessionBroadcast((message) => {
      if (message.type === 'logout') {
        if (endingRef.current) return;
        if (!useAuthStore.getState().isAuthenticated) return;
        endingRef.current = true;
        setShowWarning(false);

        const returnTo = currentReturnPath();
        if (message.reason === INACTIVITY_LOGOUT_REASON) {
          persistLoginContinuation({ reason: INACTIVITY_LOGOUT_REASON, returnTo });
        }
        suppressSessionBroadcast(() => {
          useAuthStore.getState().clearAuth();
        });
        void queryClient.cancelQueries();
        queryClient.clear();
        if (message.reason === INACTIVITY_LOGOUT_REASON) {
          useAuthStore.getState().setRedirectAfterLogin(returnTo);
        }
        return;
      }

      if (message.type === 'lock') {
        suppressSessionBroadcast(() => {
          useAuthStore.getState().adoptRemoteLock(message);
        });
        setShowWarning(false);
        return;
      }

      suppressSessionBroadcast(() => {
        useAuthStore.getState().adoptRemoteUnlock(message);
      });
    });
  }, [isAuthenticated, queryClient]);

  const handleLogout = () => {
    if (endingRef.current) return;
    endingRef.current = true;
    setShowWarning(false);
    void signOut();
  };

  const handleExtendSession = () => {
    const now = Date.now();
    const last = readIdleClock();
    if (last !== null) {
      const since = now - last;
      if (since >= logoutMsRef.current) {
        void logoutDueToInactivity();
        return;
      }
      if (since >= lockMsRef.current) {
        setShowWarning(false);
        void lockDueToInactivity();
        return;
      }
    }
    writeIdleClock(now);
    lastActivityUpdate.current = now;
    setShowWarning(false);
  };

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${String(minutes)}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercent = warnMs === 0 ? 0 : (timeRemaining / warnMs) * 100;

  if (!isAuthenticated) return null;

  return (
    <>
      {isLocked && <LockScreen />}
      <Dialog
        open={showWarning && !isLocked}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderTop: 4, borderColor: 'warning.main' },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6" component="span" fontWeight={600}>
            {t('session.lockingSoon')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('session.lockingSoonMessage')}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('session.timeRemaining')}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={timeRemaining < 60000 ? 'error.main' : 'text.primary'}
              >
                {formatTime(timeRemaining)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              color={timeRemaining < 60000 ? 'error' : 'warning'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t('session.autoLockWarning')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={handleLogout}>
            {t('logout')}
          </Button>
          <Button variant="contained" onClick={handleExtendSession}>
            {t('session.extendSession')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
